/**
 * Desglose Pro por fuente + actividad post-Pro.
 * Vive en Analytics → Suscripciones (no en Dashboard).
 */

import { Link } from 'react-router-dom';
import { MetricCard } from '@/components/shared/MetricCard';
import {
    useProLifecycleAnalytics,
    proSourceLabel,
} from '@/hooks/useProLifecycleAnalytics';
import {
    useAnalyticsDashboard,
} from '@/hooks/useAdminAnalytics';
import {
    CreditCard, Ticket, Gift, UserCog, Crown, ArrowRight,
} from 'lucide-react';

interface ProSourcePanelProps {
    days?: number;
}

export function ProSourcePanel({ days = 30 }: ProSourcePanelProps) {
    const { data: dashboardData } = useAnalyticsDashboard('month');
    const metrics = dashboardData?.metrics;
    const { data: proLife } = useProLifecycleAnalytics(days);

    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Crown size={16} className="text-amber-400" />
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">Pro por fuente (activos ahora)</h3>
                        <p className="text-[11px] text-muted-foreground">
                            Snapshot actual · actividad post-Pro en los últimos {days} días
                        </p>
                    </div>
                </div>
                <Link to="/subscriptions" className="text-xs text-primary hover:underline flex items-center gap-1">
                    Gestionar <ArrowRight size={12} />
                </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricCard title="Pago" value={metrics?.pro_paid_active ?? 0} icon={<CreditCard size={16} />} iconColor="text-emerald-400" className="p-3" />
                <MetricCard title="Cupón" value={metrics?.pro_coupon_active ?? 0} icon={<Ticket size={16} />} iconColor="text-primary" className="p-3" />
                <MetricCard title="Admin manual" value={metrics?.pro_admin_active ?? 0} icon={<UserCog size={16} />} iconColor="text-indigo-400" className="p-3" />
                <MetricCard title="Regalo onboarding" value={metrics?.pro_gift_active ?? 0} icon={<Gift size={16} />} iconColor="text-amber-400" className="p-3" />
            </div>

            {proLife && (
                <div className="space-y-3 pt-1 border-t border-border/60">
                    <p className="text-xs text-muted-foreground">
                        Actividad media 30d con Pro vs 30d después de perderlo
                        (muestra: {proLife.post_pro_activity.sample_users} usuarios que terminaron Pro en {days}d)
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                        <MiniCompare
                            label="Días activos"
                            withPro={proLife.post_pro_activity.avg_active_days_with_pro}
                            afterPro={proLife.post_pro_activity.avg_active_days_after_pro}
                        />
                        <MiniCompare
                            label="Sesiones"
                            withPro={proLife.post_pro_activity.avg_sessions_with_pro}
                            afterPro={proLife.post_pro_activity.avg_sessions_after_pro}
                        />
                        <MiniCompare
                            label="Capturas"
                            withPro={proLife.post_pro_activity.avg_captures_with_pro}
                            afterPro={proLife.post_pro_activity.avg_captures_after_pro}
                        />
                        <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Siguen activos (+7d)</p>
                            <p className="text-sm font-bold text-foreground mt-0.5">
                                {proLife.post_pro_activity.still_active_after_7d_pct ?? '—'}%
                            </p>
                        </div>
                    </div>
                    {proLife.recent_ended.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs min-w-[560px]">
                                <thead>
                                    <tr className="text-muted-foreground border-b border-border">
                                        <th className="text-left py-1.5 font-medium">Fin Pro</th>
                                        <th className="text-left py-1.5 font-medium">Fuente</th>
                                        <th className="text-right py-1.5 font-medium">Días c/ Pro</th>
                                        <th className="text-right py-1.5 font-medium">Días s/ Pro</th>
                                        <th className="text-right py-1.5 font-medium">Ses. c/</th>
                                        <th className="text-right py-1.5 font-medium">Ses. s/</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {proLife.recent_ended.slice(0, 12).map((row) => (
                                        <tr key={`${row.user_id}-${row.ended_at}`} className="border-b border-border/40">
                                            <td className="py-1.5 text-foreground">
                                                {row.ended_at ? new Date(row.ended_at).toLocaleDateString('es-ES') : '—'}
                                            </td>
                                            <td className="py-1.5 text-muted-foreground">
                                                {proSourceLabel(row.source, proLife.source_labels)}
                                            </td>
                                            <td className="py-1.5 text-right text-emerald-400">{row.active_days_with_pro}</td>
                                            <td className="py-1.5 text-right text-rose-400/90">{row.active_days_after_pro}</td>
                                            <td className="py-1.5 text-right">{row.sessions_with_pro}</td>
                                            <td className="py-1.5 text-right">{row.sessions_after_pro}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function MiniCompare({
    label,
    withPro,
    afterPro,
}: {
    label: string;
    withPro: number | null;
    afterPro: number | null;
}) {
    return (
        <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-sm font-bold text-foreground mt-0.5">
                <span className="text-emerald-400">{withPro ?? '—'}</span>
                <span className="text-muted-foreground font-normal"> → </span>
                <span className="text-rose-400/90">{afterPro ?? '—'}</span>
            </p>
            <p className="text-[10px] text-muted-foreground">con Pro → sin Pro</p>
        </div>
    );
}
